// src/App.jsx
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import PasswordModal from './components/PasswordModal';
import RoleSelectionScreen from './components/RoleSelectionScreen';
import WelcomePage from './components/WelcomePage';
import SkeletonLoader from './components/TripPlanner/UI/SkeletonLoader';
import NotificationProvider from './context/NotificationContext.tsx';
import NotificationContainer from './components/Notifications/NotificationContainer.tsx';
import {
  isAuthenticated as hasValidToken,
  isServerLoginConfigured,
  getTokenExpiry,
  logout as authLogout,
  AUTH_EXPIRED_EVENT,
} from './services/authClient';

// Route-level code splitting: each role's app loads its own bundle on demand,
// so the auth gate / role picker stay small and fast on first paint.
const CustomerApp = lazy(() => import('./components/CustomerApp.jsx'));
const SupportWrapper = lazy(() => import('./support/SupportWrapper.jsx'));

const FullscreenSpinner = () => (
  <div className="flex items-center justify-center h-screen bg-gray-100">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    // Check if already authenticated in this session. In server-login mode
    // the source of truth is a valid (unexpired) token; the sessionStorage
    // flag alone is not enough.
    const flag = sessionStorage.getItem('authenticated') === 'true';
    const authenticated = isServerLoginConfigured() ? hasValidToken() : flag;
    const savedRole = sessionStorage.getItem('userRole');
    const welcomeShown = sessionStorage.getItem('welcomeShown');

    if (authenticated) {
      setIsAuthenticated(true);
      if (savedRole) {
        setRole(savedRole);
        if (savedRole === 'customer' && welcomeShown !== 'true') {
          setShowWelcome(true);
        }
      }
    }
    setLoading(false);
  }, []);

  const handleLogout = useCallback(() => {
    authLogout();
    sessionStorage.removeItem('authenticated');
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('welcomeShown');
    setIsAuthenticated(false);
    setRole(null);
    setShowWelcome(false);
    setShowSkeleton(false);
  }, []);

  // Sign out automatically when the token expires or any API call reports a
  // 401 (authClient dispatches AUTH_EXPIRED_EVENT).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onExpired = () => handleLogout();
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);

    let timer;
    const exp = getTokenExpiry();
    if (exp) {
      timer = setTimeout(handleLogout, Math.max(0, exp - Date.now()));
    }
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, handleLogout]);

  const handleCorrectPassword = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('authenticated', 'true');
  };

  const handleSelectRole = (selectedRole) => {
    setRole(selectedRole);
    sessionStorage.setItem('userRole', selectedRole);

    if (selectedRole === 'customer') {
      setShowWelcome(true);
    }
  };

  const handleWelcomeContinue = () => {
    sessionStorage.setItem('welcomeShown', 'true');
    setShowWelcome(false);

    setShowSkeleton(true);
    setTimeout(() => {
      setShowSkeleton(false);
    }, 24000);
  };

  if (loading) {
    return <FullscreenSpinner />;
  }

  // Render based on authentication and role selection state
  const renderContent = () => {
    if (!isAuthenticated) {
      return <PasswordModal onCorrectPassword={handleCorrectPassword} />;
    } else if (!role) {
      return <RoleSelectionScreen onSelectRole={handleSelectRole} />;
    } else if (role === 'customer') {
      if (showWelcome) {
        return <WelcomePage onContinue={handleWelcomeContinue} />;
      } else if (showSkeleton) {
        return <SkeletonLoader />;
      } else {
        return (
          <Suspense fallback={<SkeletonLoader />}>
            <CustomerApp />
          </Suspense>
        );
      }
    } else if (role === 'support') {
      return (
        <Suspense fallback={<FullscreenSpinner />}>
          <SupportWrapper />
        </Suspense>
      );
    }

    // Fallback
    return <div>Something went wrong. Please refresh the page.</div>;
  };

  return (
    <NotificationProvider>
      {renderContent()}
      <NotificationContainer />
    </NotificationProvider>
  );
}

export default App;
